import { create } from 'zustand'
import type { AidaMediaKind, AidaMediaStatus } from '../../types'
import type { PromptDetails } from '../../utils/prompt-details'
import { lightboxIndexAfterStep } from '../../utils/media-lightbox'

interface MediaDownloadFileOptions {
  url: string
  fileName: string
}

export interface AidaMediaLightboxItem {
  url: string
  fileName: string
  downloadFile?: (options: MediaDownloadFileOptions) => Promise<void>
  ref?: string
  kind?: AidaMediaKind
  model?: string
  status?: AidaMediaStatus
  posterUrl?: string
  caption?: string
  removable?: boolean
  onRemove?: () => void
  onShowInChat?: () => void
  details?: PromptDetails
  onEditPrompt?: (draft: string) => void
  onStartNewChat?: (origin: HTMLElement) => void
}

interface AidaMediaLightboxState {
  item: AidaMediaLightboxItem | null
  playlist: AidaMediaLightboxItem[]
  index: number
}

export const useAidaMediaLightboxStore = create<AidaMediaLightboxState>(() => ({
  item: null,
  playlist: [],
  index: 0,
}))

let suppressAidaCloseUntil = 0

export function openAidaMediaLightbox(
  item: AidaMediaLightboxItem,
  gallery?: readonly AidaMediaLightboxItem[],
) {
  suppressAidaCloseUntil = 0
  const playlist = gallery && gallery.length > 0 ? [...gallery] : [item]
  const found = playlist.findIndex(entry =>
    item.ref ? entry.ref === item.ref : entry.url === item.url,
  )
  const index = Math.max(found, 0)
  useAidaMediaLightboxStore.setState({
    item: playlist[index] ?? item,
    playlist,
    index,
  })
}

export function openAidaMediaPlaylist(playlist: AidaMediaLightboxItem[], index: number) {
  const item = playlist[index]
  if (!item) {
    return
  }
  suppressAidaCloseUntil = 0
  useAidaMediaLightboxStore.setState({ item, playlist, index })
}

export function stepAidaMediaLightbox(delta: number) {
  const state = useAidaMediaLightboxStore.getState()
  const index = lightboxIndexAfterStep(state.index, delta, state.playlist.length)
  const item = state.playlist[index]
  if (index === state.index || !item) {
    return
  }
  useAidaMediaLightboxStore.setState({ item, index })
}

export function suppressAidaClose(ms = 400) {
  suppressAidaCloseUntil = performance.now() + ms
}

export function closeAidaMediaLightbox(options?: { suppressAidaClose?: boolean }) {
  // Overlay unmounts mid-click; the same pointer then hits Aida's close underneath.
  if (options?.suppressAidaClose) {
    suppressAidaClose()
  } else {
    suppressAidaCloseUntil = 0
  }
  useAidaMediaLightboxStore.setState({ item: null, playlist: [], index: 0 })
}

export function isAidaCloseSuppressed() {
  return (
    useAidaMediaLightboxStore.getState().item !== null || performance.now() < suppressAidaCloseUntil
  )
}
